import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, adminHeaders } from "../lib/api";
import { DEFAULT_CONTENT } from "../lib/defaultContent";
import PreviewBanner from "../components/PreviewBanner";

const ContentContext = createContext({ content: DEFAULT_CONTENT, refresh: () => {}, isPreview: false });

const CACHE_KEY = "tsl_content_v2";
const CACHE_FRESH_MS = 5 * 60 * 1000; // copy from cache is fine; artwork isn't after this

// Editorial images are versioned URLs, so a cached copy can point at artwork the
// curator has since replaced. Drop them and let the live fetch fill them in —
// a dark hero for ~200ms beats a flash of the previous image.
const withoutImages = (node) => {
  if (Array.isArray(node)) return node.map(withoutImages);
  if (node && typeof node === "object") {
    return Object.fromEntries(
      Object.entries(node)
        .filter(([, v]) => !(typeof v === "string" && v.startsWith("/api/content/image/")))
        .map(([k, v]) => [k, withoutImages(v)]),
    );
  }
  return node;
};

const readCache = () => {
  try {
    const c = localStorage.getItem(CACHE_KEY);
    if (!c) return null;
    const { at, data } = JSON.parse(c);
    if (!data) return null;
    return Date.now() - (at || 0) < CACHE_FRESH_MS ? data : withoutImages(data);
  } catch (e) {
    console.warn("Could not read cached site content", e);
    return null;
  }
};

const previewActive = () =>
  typeof window !== "undefined" && new URLSearchParams(window.location.search).get("preview") === "1";

export function ContentProvider({ children }) {
  const isPreview = previewActive();
  // Seed from the last-seen live content so banners/copy render correctly on
  // load instead of briefly flashing the built-in defaults.
  const [content, setContent] = useState(() => (!isPreview && readCache()) || DEFAULT_CONTENT);

  const refresh = useCallback(() => {
    if (isPreview) {
      api
        .get("/admin/content/draft", { headers: adminHeaders() })
        .then((r) => setContent(r.data.content))
        .catch(() => api.get("/content").then((r) => setContent(r.data)).catch((e) => console.error("Could not load site content", e)));
    } else {
      api
        .get("/content")
        .then((r) => {
          setContent(r.data);
          try {
            localStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), data: r.data }));
          } catch (e) {
            console.warn("Could not cache site content", e);
          }
        })
        .catch((e) => console.error("Could not load site content", e));
    }
  }, [isPreview]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <ContentContext.Provider value={{ content, refresh, isPreview }}>
      {isPreview && <PreviewBanner />}
      {children}
    </ContentContext.Provider>
  );
}

export const useContent = () => useContext(ContentContext);
